import { useState } from "react";
import { IconButton, Stack, Typography } from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { ConfirmationModal } from "@packsize/components";
import { FormattedMessage, useIntl } from "react-intl";
import { type Role } from "@packsize/identity-api-sdk";
import { useToast } from "@packsize/toast";

import { useDeleteRole } from "@/hooks/roles/use-delete-role";

interface DeleteRoleButtonProps {
	readonly role: Role;
}

export const DeleteRoleButton = ({ role }: DeleteRoleButtonProps) => {
	const { mutateAsync: deleteRole, isPending } = useDeleteRole();
	const [open, setOpen] = useState(false);
	const intl = useIntl();
	const { toastSuccess, toastError } = useToast();

	const handleClose = () => {
		setOpen(false);
	};
	const handleOpen = () => {
		setOpen(true);
	};

	const onConfirm = async () => {
		try {
			await deleteRole(role);
			toastSuccess({ message: intl.formatMessage({ id: "Role Deleted", defaultMessage: "Role Deleted" }) });
			handleClose();
		} catch {
			toastError({
				message: intl.formatMessage({ id: "Failed to Delete Role", defaultMessage: "Failed to Delete Role" }),
			});
		}
	};

	return (
		<>
			<IconButton onClick={handleOpen}>
				<DeleteOutlinedIcon />
			</IconButton>

			<ConfirmationModal
				open={open}
				size="sm"
				onClose={handleClose}
				onCancel={handleClose}
				onConfirm={onConfirm}
				title={intl.formatMessage({
					id: "Delete Role",
					defaultMessage: "Delete Role",
				})}
				slotProps={{
					confirmButton: { loading: isPending },
				}}
				confirmText={intl.formatMessage({ id: "Delete", defaultMessage: "Delete" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
			>
				<Stack spacing={1.5}>
					<Typography sx={{ textAlign: "justify" }}>
						<FormattedMessage id="This Change Cannot Be Undone." defaultMessage="This Change Cannot Be Undone." />
					</Typography>
				</Stack>
			</ConfirmationModal>
		</>
	);
};
